<svelte:options
  customElement={{
    tag: "oc-banner-v1",
    shadow: "none",
    /*                                            */
    extend: window.__components.extend({ delegateFocus: true }),
    props: {
      aria: { type: "Object" },
      headline: { type: "String" },
      hideCloseButton: { type: "Boolean", attribute: "hide-close-button", reflect: true },
      hideIcon: { type: "Boolean", attribute: "hide-icon", reflect: true },
      iconType: { type: "String", attribute: "icon-type" },
      size: { type: "String", reflect: true },
      variant: { type: "String", reflect: true },
      hidden: { type: "Boolean", reflect: true },
    },
  }}
/>

<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { iconTypeByBannerType } from "./consts";
  import { usePropertyChange } from "../../../common/utils/usePropertyChange";
  import type { Props } from "./BannerV1.types.js";
  import type { CommonProps } from "../../../common/component.types.js";
  import { useAriaAttributes } from "../../../common/utils/aria/useAriaAttributes.js";
  import { applyAttributesToHost } from "../../../common/utils/aria/applyAttributesToHost.js";

  usePropertyChange();

  export let aria: Required<CommonProps>["aria"] = {};
  $: {
    const ariaAttributes = useAriaAttributes(aria);
    applyAttributesToHost(ariaAttributes.default);
  }

  export let headline: Props["headline"] = undefined;
  export let hideCloseButton: Props["hideCloseButton"] = false;
  export let hideIcon: Props["hideIcon"] = false;
  export let iconType: Props["iconType"] = undefined;
  export let size: Props["size"] = "100";
  export let variant: Exclude<Props["variant"], undefined> = "info";
  export let hidden = false;

  $: computedIconType = iconType || iconTypeByBannerType[variant];

  const dispatch = createEventDispatcher<{
    "oc-close": undefined;
  }>();

  export function handleClose(ev: KeyboardEvent | MouseEvent) {
    if (
      ev.type === "click" ||
      (ev.type === "keyup" && "code" in ev && (ev.code === "Space" || ev.code === "Enter"))
    ) {
      dispatch("oc-close");
      hidden = true;
    }
  }

  $: {
    aria.hidden = hidden ? "true" : undefined;
  }
</script>

{#if !hideIcon}
  <oc-icon-v1 class="banner__icon" type={computedIconType} aria={{ hidden: "true" }} />
{/if}

<slot />
{#if $$slots.actions}
  <div class="banner__actions">
    <slot name="actions" />
  </div>
{/if}

{#if !hideCloseButton}
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <oc-icon-button-v1
    class="banner__close-button"
    iconType="close"
    size={"50"}
    transparent
    on:click={handleClose}
    on:keyup={handleClose}
    aria={{ label: "schließen" }}
  ></oc-icon-button-v1>
{/if}

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .banner__icon {
    position: absolute;
  }

  .banner__actions {
    position: relative;
    display: flex;
    flex-wrap: nowrap; /*                        */
  }

  .banner__close-button {
    position: absolute;
    top: tokens.$oc-semantic-spacing-75;
    right: tokens.$oc-semantic-spacing-75;
  }

  :host([size="100"]) {
    .banner__icon {
      top: tokens.$oc-component-banner-100-spacing;
      left: tokens.$oc-component-banner-100-spacing;
      width: tokens.$oc-component-banner-100-icon-size;
      height: tokens.$oc-component-banner-100-icon-size;
    }

    .banner__actions {
      margin-top: tokens.$oc-component-banner-100-gap-y;
      gap: tokens.$oc-component-banner-100-gap-y tokens.$oc-component-banner-100-gap-x;
    }
  }

  :host([size="200"]) {
    .banner__icon {
      top: tokens.$oc-component-banner-200-spacing;
      left: tokens.$oc-component-banner-200-spacing;
      width: tokens.$oc-component-banner-200-icon-size;
      height: tokens.$oc-component-banner-200-icon-size;
    }

    .banner__actions {
      bottom: calc(-1 * tokens.$oc-semantic-spacing-25); /*                                  */
      margin-top: tokens.$oc-component-banner-200-gap-y;
      gap: tokens.$oc-component-banner-200-gap-y tokens.$oc-component-banner-200-gap-x;
      justify-content: right;
    }
  }

  :host([size="300"]) {
    .banner__icon {
      top: tokens.$oc-component-banner-300-spacing;
      left: calc(50% - tokens.$oc-component-banner-300-icon-size / 2);
      width: tokens.$oc-component-banner-300-icon-size;
      height: tokens.$oc-component-banner-300-icon-size;
    }

    .banner__actions {
      bottom: calc(-1 * tokens.$oc-semantic-spacing-50); /*                                  */
      margin-top: tokens.$oc-component-banner-300-gap-y;
      gap: tokens.$oc-component-banner-300-gap-y tokens.$oc-component-banner-300-gap-x;
      justify-content: right;
    }
  }

  :host([variant="error"]) {
    .banner__icon {
      fill: tokens.$oc-component-banner-error-icon-color;
    }
  }

  :host([variant="success"]) {
    .banner__icon {
      fill: tokens.$oc-component-banner-success-icon-color;
    }
  }

  :host([variant="warning"]) {
    .banner__icon {
      fill: tokens.$oc-component-banner-warning-icon-color;
    }
  }

  :host([variant="hint"]) {
    .banner__icon {
      fill: tokens.$oc-component-banner-hint-icon-color;
    }
  }

  :host([variant="info"]) {
    .banner__icon {
      fill: tokens.$oc-component-banner-info-icon-color;
    }
  }
</style>
